/**
 * Studio settings. Spec 11.3: "Display name, locale, default board/rule set, notification
 * preferences."
 *
 * Everything here is `identity.updateProfile` (spec 8.2) — the studio has no settings of its
 * own, because a facilitator is a user with a role rather than a separate account type.
 *
 * Default board and rule set are deliberately absent. There is one board and one rule set
 * (spec 7.4: "no game_templates table — there is one game"), so a picker would offer a
 * choice of one; when a second ships, `session.create` already accepts `boardId` and
 * `ruleSetId` and this is where the preference belongs.
 */
import { zodResolver } from '@hookform/resolvers/zod';
import { LOCALES, identityUpdateProfileInput } from '@lila/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect, type ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';

import { Alert } from '../../../components/ui/Alert';
import { Button } from '../../../components/ui/Button';
import { Card } from '../../../components/ui/Card';
import { Field } from '../../../components/ui/Field';
import { CheckboxField, SelectField } from '../../../components/ui/FieldParts';
import { SkeletonList } from '../../../components/ui/Skeleton';
import { identityQueryKey, useIdentity } from '../../../features/auth/useAuth';
import { MfaCard } from '../../../features/identity/MfaCard';
import { ADMIN_MFA_ENFORCED } from '../../../features/identity/mfaFlag';
import { errorKeyFor } from '../../../lib/errors';
import { trpcClient } from '../../../lib/trpc';

export const Route = createFileRoute('/$lang/studio/settings')({ component: Page });

type FormValues = z.input<typeof identityUpdateProfileInput>;

function Page(): ReactNode {
  const { t } = useTranslation(['studio', 'common']);
  const { identity, isLoading } = useIdentity();
  const queryClient = useQueryClient();

  /*
   * The three generics are load-bearing under `exactOptionalPropertyTypes`: a schema with
   * optional fields has different input and output types, and collapsing them makes the
   * resolver unassignable. Same reasoning as `LoginForm`.
   */
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<FormValues, unknown, z.output<typeof identityUpdateProfileInput>>({
    resolver: zodResolver(identityUpdateProfileInput),
  });

  /* Populated once identity arrives, rather than rendering an empty form over real values. */
  useEffect(() => {
    if (identity === null) return;
    reset({
      displayName: identity.displayName,
      locale: identity.locale,
      timezone: identity.timezone,
      reducedMotion: identity.reducedMotion,
    });
  }, [identity, reset]);

  const save = useMutation({
    mutationFn: (values: z.output<typeof identityUpdateProfileInput>) =>
      trpcClient.identity.updateProfile.mutate(values),
    onSuccess: (updated) => {
      /* Seeded rather than invalidated: the mutation already returned the new identity. */
      queryClient.setQueryData(identityQueryKey, updated);
      reset({
        displayName: updated.displayName,
        locale: updated.locale,
        timezone: updated.timezone,
        reducedMotion: updated.reducedMotion,
      });
    },
  });

  if (isLoading) {
    return <SkeletonList rows={4} label={t('studio:settings.title')} />;
  }

  return (
    <div className="flex max-w-xl flex-col gap-6">
      <h1 className="text-xl font-semibold text-ink">{t('studio:settings.title')}</h1>

      <form
        noValidate
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          /* `handleSubmit` returns a promise; a DOM handler must not hand one back. */
          void handleSubmit((values) => {
            save.mutate(values);
          })(event);
        }}
      >
        <Card title={t('studio:settings.title')}>
          <div className="flex flex-col gap-4">
            <Field
              label={t('studio:settings.displayName')}
              error={errors.displayName?.message}
              {...register('displayName')}
            />

            <SelectField
              label={t('studio:settings.locale')}
              error={errors.locale?.message}
              options={LOCALES.map((locale) => ({ value: locale, label: locale.toUpperCase() }))}
              {...register('locale')}
            />

            <Field
              label={t('studio:settings.timezone')}
              hint="Europe/Berlin"
              error={errors.timezone?.message}
              {...register('timezone')}
            />
          </div>
        </Card>

        <Card title={t('studio:settings.notifications')}>
          <div className="flex flex-col gap-3">
            <CheckboxField
              label={t('studio:settings.marketing')}
              {...register('marketingConsent')}
            />
            <CheckboxField
              label={t('studio:settings.reducedMotion')}
              hint={t('studio:settings.reducedMotion')}
              {...register('reducedMotion')}
            />
          </div>
        </Card>

        {save.isError && <Alert tone="error">{t(errorKeyFor(save.error))}</Alert>}
        {save.isSuccess && !isDirty && <Alert tone="success">{t('studio:settings.saved')}</Alert>}

        <div>
          <Button type="submit" loading={save.isPending} disabled={!isDirty}>
            {t('studio:settings.save')}
          </Button>
        </div>
      </form>

      {/*
       * Enrolment's only entry point; device management moved to the profile screen. Hidden
       * while two-factor is turned off (`mfaFlag`) so the UI shows nothing the API no longer
       * asks for — the component is kept, not deleted.
       */}
      {ADMIN_MFA_ENFORCED && <MfaCard />}
    </div>
  );
}
